import {
	Clock,
	FileText,
	Percent,
	Plus,
	TrendingDown,
	TrendingUp,
} from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useBaseForecast } from "@/hooks/useBaseForecast";
import { useFormatting } from "@/hooks/useFormatting";
import { useScenarios } from "@/hooks/useScenarios";
import { useVisibleScenarios } from "@/hooks/useVisibleScenarios";
import { normalizeAdjustment } from "@/lib/normalizeAdjustment";
import { calculateScenarioMetrics } from "@/lib/scenarioEngine";
import type { ScenarioMetrics } from "@/types/scenarios";
import { AdjustmentCard } from "./AdjustmentCard";
import { CollapsibleGroup } from "./CollapsibleGroup";
import {
	AddScenarioTransactionForm as AddScenarioCostTransactionForm,
	AdjustCostsByPercentForm,
	AdjustSpecificTransactionForm as AdjustSpecificCostTransactionForm,
} from "./CostForms";
import {
	AddScenarioTransactionForm,
	AdjustRevenueByPercentForm,
	AdjustSpecificTransactionForm,
} from "./RevenueForms";
import { ScenarioImpactPreview } from "./ScenarioImpactPreview";

interface ScenarioBuilderProps {
	scenarioId: string;
	onAdjustmentCountChange?: (count: number) => void;
	onLiveMetricsChange?: (metrics: ScenarioMetrics | null) => void;
	readOnly?: boolean;
}

type RevenueFormType =
	| "adjust-percent"
	| "adjust-transaction"
	| "add-scenario-transaction"
	| null;
type CostFormType =
	| "adjust-percent"
	| "adjust-transaction"
	| "add-scenario-transaction"
	| null;

export const ScenarioBuilder: React.FC<ScenarioBuilderProps> = ({
	scenarioId,
	onAdjustmentCountChange,
	onLiveMetricsChange,
	readOnly = false,
}) => {
	const { formatCurrency } = useFormatting();
	const scenarios = useVisibleScenarios();
	const scenariosRef = useRef(scenarios);
	scenariosRef.current = scenarios;
	const { updateScenario, createVersion } = useScenarios();

	const loadedScenarioId = useRef<string>("");

	// Base forecast — from shared context for engine calculations
	const { baseForecast } = useBaseForecast();

	// Revenue state
	const [revenueAdjustments, setRevenueAdjustments] = useState<any[]>([]);
	const [activeRevenueForm, setActiveRevenueForm] =
		useState<RevenueFormType>(null);
	const [liveRevenuePreview, setLiveRevenuePreview] = useState<any>(null);
	const [editingRevenueAdjustment, setEditingRevenueAdjustment] = useState<
		any | null
	>(null);

	// Pending delete state (items stay in list until timeout fires)
	const [pendingDeleteItem, setPendingDeleteItem] = useState<{ id: number; name: string; type: 'revenue' | 'cost' } | null>(null);

	// Cost state
	const [costAdjustments, setCostAdjustments] = useState<any[]>([]);
	const [activeCostForm, setActiveCostForm] = useState<CostFormType>(null);
	const [liveCostPreview, setLiveCostPreview] = useState<any>(null);
	const [editingCostAdjustment, setEditingCostAdjustment] = useState<
		any | null
	>(null);

	// Normalize live adjustments into ScenarioChange[] for the engine
	const normalizedChanges = useMemo(() => {
		const allAdjustments = [...revenueAdjustments, ...costAdjustments];
		if (allAdjustments.length === 0) return [];
		return allAdjustments.map(normalizeAdjustment);
	}, [revenueAdjustments, costAdjustments]);

	// Compute and emit live metrics whenever normalized changes update
	useEffect(() => {
		if (!onLiveMetricsChange) return;
		if (normalizedChanges.length === 0) {
			onLiveMetricsChange(null);
			return;
		}
		const metrics = calculateScenarioMetrics(baseForecast, normalizedChanges);
		onLiveMetricsChange(metrics);
	}, [normalizedChanges, baseForecast, onLiveMetricsChange]);

	// Load existing changes from scenario — only once per scenarioId
	// Use scenariosRef.current so this effect does NOT re-run when scenarios array reference changes
	useEffect(() => {
		if (loadedScenarioId.current === scenarioId) return;
		loadedScenarioId.current = scenarioId;

		const scenario = scenariosRef.current.find((s) => s.id === scenarioId);
		if (scenario && scenario.changes) {
			// Filter revenue adjustments
			const revenueChanges = scenario.changes.filter(
				(change) =>
					change.type === "add-revenue" ||
					change.type === "adjust-revenue" ||
					change.type === "change-revenue" ||
					change.type === "win-customer",
			);

			// Filter cost adjustments
			const costChanges = scenario.changes.filter(
				(change) =>
					change.type === "change-costs" ||
					change.type === "delay-payment" ||
					change.type === "add-cost" ||
					change.type === "adjust-cost" ||
					change.type === "hire-employees" ||
					change.type === "capital-purchase",
			);

			setRevenueAdjustments(revenueChanges);
			setCostAdjustments(costChanges);
		}
	}, [scenarioId]);

	// Calculate total adjustment count and notify parent
	useEffect(() => {
		const totalCount = revenueAdjustments.length + costAdjustments.length;

		if (onAdjustmentCountChange) {
			onAdjustmentCountChange(totalCount);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [revenueAdjustments, costAdjustments]);

	/**
	 * Convert a canonical (API) adjustment back into the flat shape that
	 * the builder forms expect, so every field pre-populates on edit.
	 */
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const denormalizeForEdit = (adj: any): any => {
		const details = adj.details || {};
		switch (adj.type) {
			// Revenue: % adjustment
			case "change-revenue":
				return {
					...adj,
					type: "adjust-revenue-percent",
					adjustmentPercent: details.percentage ?? 0,
					entity: details.entity ?? "All Entities",
					effectiveDate: adj.effectiveDate
						? new Date(adj.effectiveDate)
						: adj.date
							? new Date(adj.date)
							: undefined,
					reason: adj.description ?? "",
					confidence: details.confidence ?? "probable",
					adjustmentAmount: adj.amount ?? 0,
					monthlyImpact: details.monthlyAmount ?? 0,
					estimatedCurrentRevenue: 1200000,
				};
			// Cost: % adjustment
			case "change-costs":
				return {
					...adj,
					type: "adjust-costs-percent",
					adjustmentPercent: details.percentage ?? 0,
					category: details.category ?? "",
					entity: details.entity ?? "All Entities",
					effectiveDate: adj.effectiveDate
						? new Date(adj.effectiveDate)
						: adj.date
							? new Date(adj.date)
							: undefined,
					reason: adj.description ?? "",
					confidence: details.confidence ?? "probable",
					monthlyImpact: details.monthlySavings
						? -Math.abs(details.monthlySavings)
						: details.monthlyIncrease
							? Math.abs(details.monthlyIncrease)
							: 0,
					annualImpact: adj.amount ?? 0,
				};
			// Revenue / Cost: adjust specific transaction
			case "adjust-revenue":
			case "adjust-cost":
				return {
					...adj,
					type: "adjust-transaction",
					transactionId: details.transactionId ?? "",
					adjustedValue: adj.amount ?? 0,
					newDueDate: adj.newDueDate ? new Date(adj.newDueDate) : undefined,
					dateChangeDays: adj.delayDays ?? 0,
					valueChange: details.valueChange ?? 0,
					reason: adj.description ?? "",
					confidence: details.confidence ?? "probable",
				};
			// Revenue: add scenario transaction
			case "add-revenue":
				return {
					...adj,
					type: "add-scenario-transaction",
					transactionType: details.transactionType ?? "",
					date: adj.date ? new Date(adj.date) : undefined,
					amount: adj.amount ?? 0,
					description: adj.name ?? "",
					reason: adj.description ?? "",
					confidence: details.confidence ?? "probable",
				};
			// Cost: add scenario transaction
			case "add-cost":
				return {
					...adj,
					type: "add-scenario-transaction",
					transactionType: details.transactionType ?? "",
					expectedDate: adj.date ? new Date(adj.date) : undefined,
					amount: adj.amount ?? 0,
					description: adj.name ?? "",
					businessContext: adj.description ?? "",
					confidence: details.confidence ?? "probable",
				};
			default:
				return adj;
		}
	};

	// Revenue handlers
	const handleSaveRevenueAdjustment = async (data: any) => {
		let newRevenue: any[];
		let isEdit = false;
		if (editingRevenueAdjustment) {
			isEdit = true;
			newRevenue = revenueAdjustments.map((adj) =>
				adj.id === editingRevenueAdjustment.id
					? { ...data, id: editingRevenueAdjustment.id }
					: adj,
			);
			setRevenueAdjustments(newRevenue);
			setEditingRevenueAdjustment(null);
		} else {
			newRevenue = [...revenueAdjustments, { ...data, id: Date.now() }];
			setRevenueAdjustments(newRevenue);
		}
		setActiveRevenueForm(null);
		setLiveRevenuePreview(null);

		const allChanges = [...newRevenue, ...costAdjustments].map(
			normalizeAdjustment,
		);
		await updateScenario(scenarioId, { changes: allChanges });
		createVersion(
			scenarioId,
			isEdit
				? `Updated revenue adjustment: ${data.name || "Untitled"}`
				: `Added revenue adjustment: ${data.name || "Untitled"}`,
		);
	};

	const handleCancelRevenueForm = () => {
		setActiveRevenueForm(null);
		setEditingRevenueAdjustment(null);
		setLiveRevenuePreview(null);
	};

	const handleRemoveRevenueAdjustment = (id: number) => {
		const removed = revenueAdjustments.find((adj) => adj.id === id);
		if (!removed) return;
		setPendingDeleteItem({ id, name: removed.name || 'Adjustment', type: 'revenue' });
	};

	const confirmDelete = async () => {
		if (!pendingDeleteItem) return;
		const { id, type } = pendingDeleteItem;
		if (type === 'revenue') {
			const newRevenue = revenueAdjustments.filter((adj) => adj.id !== id);
			setRevenueAdjustments(newRevenue);
			const allChanges = [...newRevenue, ...costAdjustments].map(normalizeAdjustment);
			await updateScenario(scenarioId, { changes: allChanges });
			createVersion(scenarioId, "Removed revenue adjustment");
		} else {
			const newCosts = costAdjustments.filter((adj) => adj.id !== id);
			setCostAdjustments(newCosts);
			const allChanges = [...revenueAdjustments, ...newCosts].map(normalizeAdjustment);
			await updateScenario(scenarioId, { changes: allChanges });
			createVersion(scenarioId, "Removed cost adjustment");
		}
		setPendingDeleteItem(null);
	};

	const handleEditRevenueAdjustment = (adjustment: any) => {
		const editData = denormalizeForEdit(adjustment);
		setEditingRevenueAdjustment(editData);
		// Determine which form to open based on original adjustment type
		if (
			adjustment.type === "adjust-revenue-percent" ||
			adjustment.type === "change-revenue"
		) {
			setActiveRevenueForm("adjust-percent");
		} else if (
			adjustment.type === "adjust-transaction" ||
			adjustment.type === "adjust-revenue"
		) {
			setActiveRevenueForm("adjust-transaction");
		} else if (
			adjustment.type === "add-scenario-transaction" ||
			adjustment.type === "add-revenue"
		) {
			setActiveRevenueForm("add-scenario-transaction");
		}
	};

	// Cost handlers
	const handleSaveCostAdjustment = async (data: any) => {
		let newCosts: any[];
		let isEdit = false;
		if (editingCostAdjustment) {
			isEdit = true;
			newCosts = costAdjustments.map((adj) =>
				adj.id === editingCostAdjustment.id
					? { ...data, id: editingCostAdjustment.id }
					: adj,
			);
			setCostAdjustments(newCosts);
			setEditingCostAdjustment(null);
		} else {
			newCosts = [...costAdjustments, { ...data, id: Date.now() }];
			setCostAdjustments(newCosts);
		}
		setActiveCostForm(null);
		setLiveCostPreview(null);

		const allChanges = [...revenueAdjustments, ...newCosts].map(
			normalizeAdjustment,
		);
		await updateScenario(scenarioId, { changes: allChanges });
		createVersion(
			scenarioId,
			isEdit
				? `Updated cost adjustment: ${data.name || "Untitled"}`
				: `Added cost adjustment: ${data.name || "Untitled"}`,
		);
	};

	const handleCancelCostForm = () => {
		setActiveCostForm(null);
		setEditingCostAdjustment(null);
		setLiveCostPreview(null);
	};

	const handleRemoveCostAdjustment = (id: number) => {
		const removed = costAdjustments.find((adj) => adj.id === id);
		if (!removed) return;
		setPendingDeleteItem({ id, name: removed.name || 'Adjustment', type: 'cost' });
	};

	const handleEditCostAdjustment = (adjustment: any) => {
		const editData = denormalizeForEdit(adjustment);
		setEditingCostAdjustment(editData);
		// Determine which form to open based on original adjustment type
		if (
			adjustment.type === "adjust-costs-percent" ||
			adjustment.type === "change-costs"
		) {
			setActiveCostForm("adjust-percent");
		} else if (
			adjustment.type === "adjust-transaction" ||
			adjustment.type === "adjust-cost"
		) {
			setActiveCostForm("adjust-transaction");
		} else if (
			adjustment.type === "add-scenario-transaction" ||
			adjustment.type === "add-cost"
		) {
			setActiveCostForm("add-scenario-transaction");
		}
	};

	// Calculate revenue summary
	// Handles both form-level types (from newly added adjustments) and
	// canonical/normalized types (from existing scenario changes loaded from context)
	const revenueSummary = revenueAdjustments.reduce((acc, adj) => {
		if (adj.type === "adjust-revenue-percent") {
			return acc + adj.monthlyImpact;
		} else if (adj.type === "change-revenue") {
			// Canonical type for adjust-revenue-percent
			const monthly = adj.details?.monthlyAmount ?? adj.amount / 12;
			return acc + monthly;
		} else if (
			adj.type === "add-scenario-transaction" ||
			adj.type === "add-revenue"
		) {
			// Form type or canonical type for adding a revenue transaction
			const monthly = adj.details?.monthlyAmount ?? adj.amount / 12;
			return acc + monthly;
		} else if (
			adj.type === "adjust-transaction" ||
			adj.type === "adjust-revenue"
		) {
			// Form type or canonical type for adjusting a specific transaction
			const valueChange = adj.valueChange ?? adj.details?.valueChange ?? 0;
			return acc + valueChange / 12;
		} else if (adj.type === "win-customer") {
			// Seed data type — one-off revenue inflow, spread as monthly equivalent
			const monthly = adj.details?.monthlyAmount ?? adj.amount / 3;
			return acc + monthly;
		}
		return acc;
	}, 0);

	const revenueSummaryText =
		revenueAdjustments.length > 0
			? `Net impact: ${revenueSummary >= 0 ? "+" : "-"}${formatCurrency(Math.round(Math.abs(revenueSummary)), "full")}/month`
			: undefined;

	// Calculate cost summary
	// Handles both form-level types and canonical/normalized types
	const costSummary = costAdjustments.reduce((acc, adj) => {
		if (adj.type === "adjust-costs-percent") {
			return acc + adj.monthlyImpact; // Negative for increases, positive for reductions
		} else if (adj.type === "change-costs") {
			const monthlySavings = adj.details?.monthlySavings;
			const monthlyIncrease = adj.details?.monthlyIncrease;
			// If structured details present, use them
			if (monthlySavings !== undefined || monthlyIncrease !== undefined) {
				if (adj.details?.changeType === "reduction") {
					return acc + Number(monthlySavings ?? 0);
				}
				return acc - Number(monthlyIncrease ?? 0);
			}
			// Fallback: raw amount field (seed data / one-off costs)
			return acc + (adj.amount ?? 0);
		} else if (
			adj.type === "add-scenario-transaction" ||
			adj.type === "add-cost"
		) {
			// Form type or canonical type for adding a cost transaction
			const monthly = adj.details?.monthlyAmount ?? adj.amount / 12;
			return acc - monthly;
		} else if (
			adj.type === "adjust-transaction" ||
			adj.type === "adjust-cost"
		) {
			// Form type or canonical type for adjusting a specific cost transaction
			const valueChange = adj.valueChange ?? adj.details?.valueChange ?? 0;
			return acc + valueChange / 12;
		} else if (adj.type === "hire-employees") {
			// Seed data type — recurring payroll cost, use amount directly as monthly
			const monthly = adj.details?.monthlyCost ?? Math.abs(adj.amount) / 3;
			return acc - monthly;
		} else if (adj.type === "capital-purchase") {
			// Seed data type — one-off capex outflow
			const monthly = Math.abs(adj.amount) / 3;
			return acc - monthly;
		}
		return acc;
	}, 0);

	// Separate recurring monthly impact from one-off raw amounts
	const costOneOffTotal = costAdjustments
		.filter(
			(adj) =>
				(adj.type === "change-costs" &&
					adj.details?.monthlySavings === undefined &&
					adj.details?.monthlyIncrease === undefined) ||
				adj.type === "capital-purchase",
		)
		.reduce((acc: number, adj) => acc + Math.abs(adj.amount ?? 0), 0);
	const costRecurringSummary = costSummary + costOneOffTotal; // restore since one-offs were subtracted

	const costSummaryText =
		costAdjustments.length > 0
			? costOneOffTotal > 0 && costRecurringSummary === 0
				? `One-off cost: -${formatCurrency(Math.round(costOneOffTotal), "full")}`
				: costOneOffTotal > 0
					? `Net impact: ${costRecurringSummary >= 0 ? "+" : "-"}${formatCurrency(Math.round(Math.abs(costRecurringSummary)), "full")}/month · One-off: -${formatCurrency(Math.round(costOneOffTotal), "full")}`
					: `Net impact: ${costSummary >= 0 ? "+" : "-"}${formatCurrency(Math.round(Math.abs(costSummary)), "full")}/month`
			: undefined;

	// Render revenue add button with dropdown
	const renderRevenueAddButton = () => {
		if (activeRevenueForm) return null;
		if (readOnly) return null;

		return (
			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<Button variant="outline" className="w-full border-dashed">
						<Plus className="w-4 h-4 mr-2" />
						Add Revenue Adjustment
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent align="start" className="w-56">
					<DropdownMenuItem
						onClick={() => setActiveRevenueForm("adjust-percent")}
					>
						<Percent className="w-4 h-4 mr-2" />
						Adjust Revenue by %
					</DropdownMenuItem>
					<DropdownMenuItem
						onClick={() => setActiveRevenueForm("adjust-transaction")}
					>
						<Clock className="w-4 h-4 mr-2" />
						Adjust Specific Transaction
					</DropdownMenuItem>
					<DropdownMenuItem
						onClick={() => setActiveRevenueForm("add-scenario-transaction")}
					>
						<FileText className="w-4 h-4 mr-2" />
						Add Scenario Transaction
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenu>
		);
	};

	// Render cost add button with dropdown
	const renderCostAddButton = () => {
		if (activeCostForm) return null;
		if (readOnly) return null;

		return (
			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<Button variant="outline" className="w-full border-dashed">
						<Plus className="w-4 h-4 mr-2" />
						Add Cost Adjustment
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent align="start" className="w-56">
					<DropdownMenuItem onClick={() => setActiveCostForm("adjust-percent")}>
						<Percent className="w-4 h-4 mr-2" />
						Adjust Costs by %
					</DropdownMenuItem>
					<DropdownMenuItem
						onClick={() => setActiveCostForm("adjust-transaction")}
					>
						<FileText className="w-4 h-4 mr-2" />
						Adjust Specific Transaction
					</DropdownMenuItem>
					<DropdownMenuItem
						onClick={() => setActiveCostForm("add-scenario-transaction")}
					>
						<Plus className="w-4 h-4 mr-2" />
						Add Scenario Transaction
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenu>
		);
	};

	return (
		<>
		<div className="flex flex-col gap-6">
			{/* BUILDER GROUPS */}
			<div className="space-y-4">
				{/* Header */}
				<div className="mb-6">
					<h2 className="text-lg font-semibold text-gray-900 mb-1">
						Scenario Builder
					</h2>
					<p className="text-sm text-gray-600">
						{readOnly
							? "View the adjustments applied to this scenario. You have read-only access."
							: "Build your scenario by adding adjustments below. Each group can contain multiple changes that will be applied to your forecast."}
					</p>
				</div>

				{/* Tip */}
				{!readOnly && (
					<div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
						<p className="text-sm text-blue-800">
							<strong>💡 Tip:</strong> Start by adding adjustments to the groups
							below. Each adjustment will automatically update the impact preview.
						</p>
					</div>
				)}

				{/* Collapsible Groups */}
				<div className="space-y-3">
					{/* 1. Revenue Adjustments */}
					<CollapsibleGroup
						icon={<TrendingUp className="w-5 h-5" />}
						title="Revenue Adjustments"
						count={revenueAdjustments.length}
						summary={revenueSummaryText}
						isEmpty={revenueAdjustments.length === 0 && !activeRevenueForm}
						customAddButton={renderRevenueAddButton()}
					>
						{/* Render adjustments with inline edit form below the card being edited */}
						{revenueAdjustments.map((adjustment) => (
							<React.Fragment key={adjustment.id}>
								{/* Hide the card when it is being edited */}
								{editingRevenueAdjustment?.id !== adjustment.id && (
									<AdjustmentCard
										adjustment={adjustment}
										onEdit={readOnly ? undefined : () => handleEditRevenueAdjustment(adjustment)}
										onRemove={readOnly ? undefined : () => handleRemoveRevenueAdjustment(adjustment.id)}
									/>
								)}
								{/* Render edit form inline directly below (replacing) the card being edited */}
								{editingRevenueAdjustment?.id === adjustment.id &&
									activeRevenueForm === "adjust-percent" && (
										<AdjustRevenueByPercentForm
											key={`edit-${adjustment.id}`}
											onSave={handleSaveRevenueAdjustment}
											onCancel={handleCancelRevenueForm}
											onLivePreview={setLiveRevenuePreview}
											initialData={editingRevenueAdjustment}
										/>
									)}
								{editingRevenueAdjustment?.id === adjustment.id &&
									activeRevenueForm === "adjust-transaction" && (
										<AdjustSpecificTransactionForm
											key={`edit-${adjustment.id}`}
											onSave={handleSaveRevenueAdjustment}
											onCancel={handleCancelRevenueForm}
											onLivePreview={setLiveRevenuePreview}
											initialData={editingRevenueAdjustment}
										/>
									)}
								{editingRevenueAdjustment?.id === adjustment.id &&
									activeRevenueForm === "add-scenario-transaction" && (
										<AddScenarioTransactionForm
											key={`edit-${adjustment.id}`}
											onSave={handleSaveRevenueAdjustment}
											onCancel={handleCancelRevenueForm}
											onLivePreview={setLiveRevenuePreview}
											initialData={editingRevenueAdjustment}
										/>
									)}
							</React.Fragment>
						))}

						{/* Render add form (only when not editing an existing adjustment) */}
						{!editingRevenueAdjustment &&
							activeRevenueForm === "adjust-percent" && (
								<AdjustRevenueByPercentForm
									onSave={handleSaveRevenueAdjustment}
									onCancel={handleCancelRevenueForm}
									onLivePreview={setLiveRevenuePreview}
								/>
							)}
						{!editingRevenueAdjustment &&
							activeRevenueForm === "adjust-transaction" && (
								<AdjustSpecificTransactionForm
									onSave={handleSaveRevenueAdjustment}
									onCancel={handleCancelRevenueForm}
									onLivePreview={setLiveRevenuePreview}
								/>
							)}
						{!editingRevenueAdjustment &&
							activeRevenueForm === "add-scenario-transaction" && (
								<AddScenarioTransactionForm
									onSave={handleSaveRevenueAdjustment}
									onCancel={handleCancelRevenueForm}
									onLivePreview={setLiveRevenuePreview}
								/>
							)}
					</CollapsibleGroup>

					{/* 2. Cost Adjustments */}
					<CollapsibleGroup
						icon={<TrendingDown className="w-5 h-5" />}
						title="Cost Adjustments"
						count={costAdjustments.length}
						summary={costSummaryText}
						isEmpty={costAdjustments.length === 0 && !activeCostForm}
						customAddButton={renderCostAddButton()}
					>
						{/* Render adjustments with inline edit form below the card being edited */}
						{costAdjustments.map((adjustment) => (
							<React.Fragment key={adjustment.id}>
								{/* Hide the card when it is being edited */}
								{editingCostAdjustment?.id !== adjustment.id && (
									<AdjustmentCard
										adjustment={adjustment}
										onEdit={readOnly ? undefined : () => handleEditCostAdjustment(adjustment)}
										onRemove={readOnly ? undefined : () => handleRemoveCostAdjustment(adjustment.id)}
									/>
								)}
								{/* Render edit form inline directly below (replacing) the card being edited */}
								{editingCostAdjustment?.id === adjustment.id &&
									activeCostForm === "adjust-percent" && (
										<AdjustCostsByPercentForm
											key={`edit-${adjustment.id}`}
											onSave={handleSaveCostAdjustment}
											onCancel={handleCancelCostForm}
											onLivePreview={setLiveCostPreview}
											initialData={editingCostAdjustment}
										/>
									)}
								{editingCostAdjustment?.id === adjustment.id &&
									activeCostForm === "adjust-transaction" && (
										<AdjustSpecificCostTransactionForm
											key={`edit-${adjustment.id}`}
											onSave={handleSaveCostAdjustment}
											onCancel={handleCancelCostForm}
											onLivePreview={setLiveCostPreview}
											initialData={editingCostAdjustment}
										/>
									)}
								{editingCostAdjustment?.id === adjustment.id &&
									activeCostForm === "add-scenario-transaction" && (
										<AddScenarioCostTransactionForm
											key={`edit-${adjustment.id}`}
											onSave={handleSaveCostAdjustment}
											onCancel={handleCancelCostForm}
											onLivePreview={setLiveCostPreview}
											initialData={editingCostAdjustment}
										/>
									)}
							</React.Fragment>
						))}

						{/* Render add form (only when not editing an existing adjustment) */}
						{!editingCostAdjustment && activeCostForm === "adjust-percent" && (
							<AdjustCostsByPercentForm
								onSave={handleSaveCostAdjustment}
								onCancel={handleCancelCostForm}
								onLivePreview={setLiveCostPreview}
							/>
						)}
						{!editingCostAdjustment &&
							activeCostForm === "adjust-transaction" && (
								<AdjustSpecificCostTransactionForm
									onSave={handleSaveCostAdjustment}
									onCancel={handleCancelCostForm}
									onLivePreview={setLiveCostPreview}
								/>
							)}
						{!editingCostAdjustment &&
							activeCostForm === "add-scenario-transaction" && (
								<AddScenarioCostTransactionForm
									onSave={handleSaveCostAdjustment}
									onCancel={handleCancelCostForm}
									onLivePreview={setLiveCostPreview}
								/>
							)}
					</CollapsibleGroup>
				</div>{/* end space-y-3 collapsible groups */}
			</div>{/* end space-y-4 builder groups */}

			{/* RISK / CONFIDENCE / SUMMARY ROW */}
			<ScenarioImpactPreview
				mode="sidebar"
				adjustments={{
					revenue: revenueAdjustments,
					cost: costAdjustments,
				}}
				livePreview={{
					revenue: liveRevenuePreview,
					cost: liveCostPreview,
				}}
				scenarioChanges={
					normalizedChanges.length > 0
						? normalizedChanges
						: (scenariosRef.current.find((s) => s.id === scenarioId)
								?.changes ?? [])
				}
			/>

			{/* FULL-WIDTH CHART */}
			<ScenarioImpactPreview
				mode="chart-only"
				adjustments={{
					revenue: revenueAdjustments,
					cost: costAdjustments,
				}}
				livePreview={{
					revenue: liveRevenuePreview,
					cost: liveCostPreview,
				}}
				scenarioChanges={
					normalizedChanges.length > 0
						? normalizedChanges
						: (scenariosRef.current.find((s) => s.id === scenarioId)
								?.changes ?? [])
				}
			/>
		</div>

		{/* Delete Confirmation Dialog */}
		<AlertDialog open={!!pendingDeleteItem} onOpenChange={(open) => { if (!open) setPendingDeleteItem(null); }}>
			<AlertDialogContent>
				<AlertDialogHeader>
					<AlertDialogTitle>Delete adjustment?</AlertDialogTitle>
					<AlertDialogDescription className="text-neutral-900">
						Are you sure you want to delete <strong>"{pendingDeleteItem?.name}"</strong>? This cannot be undone.
					</AlertDialogDescription>
				</AlertDialogHeader>
				<AlertDialogFooter>
					<AlertDialogCancel>Cancel</AlertDialogCancel>
					<AlertDialogAction onClick={confirmDelete}>Delete</AlertDialogAction>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
		</>
	);
};
